/**
 * SEO Phase 3 — Structured data (JSON-LD) helpers.
 *
 * Conservative, standards-valid schema only. Every field comes from real
 * CMS settings — nothing is invented. No FAQ/HowTo/Review/BreadcrumbList.
 *
 * Used via useJsonLd(schemaObject) — mounts a <script type="application/ld+json">
 * into <head> and removes it on unmount/re-render.
 */
import { useEffect } from "react";

const ORIGIN = typeof window !== "undefined" ? window.location.origin : "";

/** Sitewide Organization node — mounted once in App.js. */
export function buildOrganizationSchema(s) {
  if (!s) return null;
  const socials = [s.social_facebook, s.social_instagram, s.social_linkedin, s.social_x, s.social_youtube].filter(Boolean);
  const org = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "Ur Designs",
    url: ORIGIN,
    email: s.contact_email || undefined,
    telephone: s.contact_phone || undefined,
    address: {
      "@type": "PostalAddress",
      addressLocality: "Palm Beach",
      addressCountry: "AW",
    },
    ...(socials.length ? { sameAs: socials } : {}),
  };
  if (s.site_logo) {
    org.logo = s.site_logo.startsWith("http") ? s.site_logo : `${ORIGIN}${s.site_logo}`;
  }
  return org;
}

/** ProfessionalService — Home + Contact pages only. */
export function buildLocalBusinessSchema(s) {
  if (!s) return null;
  return {
    "@context": "https://schema.org",
    "@type": "ProfessionalService",
    name: "Ur Designs",
    url: ORIGIN,
    email: s.contact_email || undefined,
    telephone: s.contact_phone || undefined,
    address: {
      "@type": "PostalAddress",
      addressLocality: "Palm Beach",
      addressCountry: "AW",
    },
  };
}

/** Service — one per service configurator page. */
export function buildServiceSchema(service, s) {
  if (!service) return null;
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    name: service.name,
    description: service.short_description || service.long_description || undefined,
    provider: { "@type": "Organization", name: "Ur Designs", url: ORIGIN },
    areaServed: { "@type": "Country", name: "AW" },
    url: `${ORIGIN}/services/${service.slug}`,
    ...(service.image ? { image: service.image.startsWith("http") ? service.image : `${ORIGIN}${service.image}` } : {}),
  };
}

/** CreativeWork — portfolio case-study pages. */
export function buildCreativeWorkSchema(project) {
  if (!project) return null;
  return {
    "@context": "https://schema.org",
    "@type": "CreativeWork",
    name: project.title,
    creator: { "@type": "Organization", name: "Ur Designs", url: ORIGIN },
    url: `${ORIGIN}/portfolio/${project.slug || project.id}`,
    ...(project.image ? { image: project.image.startsWith("http") ? project.image : `${ORIGIN}${project.image}` } : {}),
    ...(project.category ? { about: project.category } : {}),
    ...(project.services_used?.length ? { keywords: project.services_used.join(", ") } : {}),
  };
}

/** VideoObject — only when the project video has reliable upload metadata. */
export function buildVideoObjectSchema(project) {
  const src = project?.homepage_video_cover;
  const m = project?.homepage_video_cover_meta;
  if (!src || !m?.uploaded_at || !project.image) return null;
  const abs = (u) => (u.startsWith("http") ? u : `${ORIGIN}${u}`);
  return {
    "@context": "https://schema.org",
    "@type": "VideoObject",
    name: `${project.title} — Project Video`,
    description: project.description || `Project video for ${project.title}${project.client ? ` (${project.client})` : ""} by Ur Designs.`,
    thumbnailUrl: [abs(project.image)],
    uploadDate: m.uploaded_at,
    contentUrl: abs(src),
    ...(m.duration > 0 ? { duration: `PT${Math.max(1, Math.round(m.duration))}S` } : {}),
    publisher: { "@type": "Organization", name: "Ur Designs", url: ORIGIN },
  };
}

/** Product — software detail + workstation use-case pages. No `offers` (pricing on request). */
export function buildProductSchema({ name, image, brand, category, description }) {
  if (!name) return null;
  return {
    "@context": "https://schema.org",
    "@type": "Product",
    name,
    ...(image ? { image: image.startsWith("http") ? image : `${ORIGIN}${image}` } : {}),
    ...(brand ? { brand: { "@type": "Brand", name: brand } } : {}),
    ...(category ? { category } : {}),
    ...(description ? { description } : {}),
  };
}

/** Mount a JSON-LD script tag. Removes on unmount or when schema changes. */
export function useJsonLd(schemaObj) {
  const json = schemaObj ? JSON.stringify(schemaObj) : null;
  useEffect(() => {
    if (!json || typeof document === "undefined") return undefined;
    const el = document.createElement("script");
    el.type = "application/ld+json";
    el.textContent = json;
    document.head.appendChild(el);
    return () => { el.parentNode?.removeChild(el); };
  }, [json]);
}
